import { useState } from 'react';

interface MRSItem {
    id: number;
    text: string;
    subscale: 'general-psych' | 'technical' | 'physical';
    reverse?: boolean;
}

const MRS_ITEMS: MRSItem[] = [
    { id: 1, text: 'I can safely perform each requirement of my military job.', subscale: 'general-psych' },
    { id: 2, text: 'I am well prepared to meet all of my military job requirements.', subscale: 'general-psych' },
    { id: 3, text: 'I am not physically prepared to meet my military job requirements.', subscale: 'physical', reverse: true },
    { id: 4, text: 'I am mentally prepared to perform each requirement of my military job.', subscale: 'general-psych' },
    { id: 5, text: 'I am ready for the requirements of my military job on a daily basis.', subscale: 'general-psych' },
    { id: 6, text: 'My technical training/education has prepared me well to perform each of my military duties.', subscale: 'technical' },
    { id: 7, text: 'I can effectively perform each requirement of my military job.', subscale: 'general-psych' },
    { id: 8, text: 'I feel psychologically ready to perform the requirements of my military job.', subscale: 'general-psych' },
    { id: 9, text: 'Physical deficiencies, illness, or injuries often derail my readiness.', subscale: 'physical', reverse: true },
    { id: 10, text: 'I am ready to perform my military job during missions, conflicts, or combat.', subscale: 'general-psych' },
    { id: 11, text: 'I am always mentally prepared to meet each of my military job requirements.', subscale: 'general-psych' },
    { id: 12, text: 'I am technically ready to perform my military job in garrison.', subscale: 'technical' },
    { id: 13, text: 'I am well prepared for future challenges in my job, including missions, conflict, or combat environments.', subscale: 'general-psych' },
    { id: 14, text: 'I am always physically prepared to meet each of my military job requirements.', subscale: 'physical' },
    { id: 15, text: 'I am technically well trained to meet each of my military job requirements.', subscale: 'technical' },
];

const RESPONSE_LABELS = ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'];

interface MRSScores {
    total: number;
    generalPsychological: number;
    technical: number;
    physical: number;
}

/* Items 3 and 9 are reverse-worded; every score (subscale and total) is
 * computed from reverse-coded values so higher always means more ready. */
function scoredValue(item: MRSItem, rawValue: number): number {
    return item.reverse ? 4 - rawValue : rawValue;
}

function computeScores(answers: Record<number, number>): MRSScores {
    const subscaleItems = (subscale: MRSItem['subscale']) => MRS_ITEMS.filter(item => item.subscale === subscale);
    const scoredSum = (items: MRSItem[]) => items.reduce((sum, item) => sum + scoredValue(item, answers[item.id]), 0);

    const generalPsychItems = subscaleItems('general-psych');
    const technicalItems = subscaleItems('technical');
    const physicalItems = subscaleItems('physical');

    return {
        generalPsychological: (scoredSum(generalPsychItems) / (generalPsychItems.length * 4)) * 100,
        technical: (scoredSum(technicalItems) / (technicalItems.length * 4)) * 100,
        physical: (scoredSum(physicalItems) / (physicalItems.length * 4)) * 100,
        total: (scoredSum(MRS_ITEMS) / (MRS_ITEMS.length * 4)) * 100,
    };
}

function ScoreBar({ label, value }: { label: string, value: number }) {
    return (
        <div className="mrsScoreRow">
            <div className="mrsScoreLabel">{label}</div>
            <div className="mrsScoreBarTrack">
                <div className="mrsScoreBarFill" style={{ width: `${value}%` }} />
            </div>
            <div className="mrsScoreValue">{value.toFixed(1)}%</div>
        </div>
    );
}

export default function MRS15() {
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const setAnswer = (itemId: number, value: number) => {
        setAnswers(prev => ({ ...prev, [itemId]: value }));
    };

    const allAnswered = MRS_ITEMS.every(item => answers[item.id] !== undefined);

    const handleSubmit = () => {
        if (!allAnswered) {
            setShowValidation(true);
            return;
        }
        setSubmitted(true);
    };

    const handleRetake = () => {
        setAnswers({});
        setSubmitted(false);
        setShowValidation(false);
    };

    if (submitted) {
        const scores = computeScores(answers);
        return (
            <>
                <div className="breadcrumb">neuroFit &gt; MRS-15</div>
                <div className="title">MRS-15</div>
                <div className="drop-shadow-content-box">
                    <div className="sectionTitle">Results</div>
                    <ScoreBar label="Total Readiness" value={scores.total} />
                    <ScoreBar label="General–Psychological" value={scores.generalPsychological} />
                    <ScoreBar label="Technical" value={scores.technical} />
                    <ScoreBar label="Physical" value={scores.physical} />
                    <button type="button" className="mrsRetakeButton" onClick={handleRetake}>Retake</button>
                </div>
                <p className="mrsCitation">
                    Taylor MK, Venkatesh A, Campbell JS, Heaton KJ, Kershaw RT, Gibbs SH, Hernandez LM. Development and Validation of the Military Readiness Scale in U.S. Naval Aircrew. <em>Mil Med</em>. Nov 1 2025;190(11-12):e2420-e2426. doi:10.1093/milmed/usaf193
                </p>
            </>
        );
    }

    return (
        <>
            <div className="breadcrumb">neuroFit &gt; MRS-15</div>
            <div className="title">MRS-15</div>
            <div className="drop-shadow-content-box">
                <p className="mrsInstructions">Reflecting on the last month, indicate how much you agree, or disagree, with the statements below.</p>
                {MRS_ITEMS.map(item => (
                    <fieldset
                        key={item.id}
                        className={`mrsItem ${showValidation && answers[item.id] === undefined ? 'mrsItemUnanswered' : ''}`}
                    >
                        <legend className="mrsItemText">{item.id}. {item.text}</legend>
                        <div className="mrsResponseOptions">
                            {RESPONSE_LABELS.map((label, value) => (
                                <label key={value} className="mrsResponseOption">
                                    <input
                                        type="radio"
                                        name={`mrs-item-${item.id}`}
                                        checked={answers[item.id] === value}
                                        onChange={() => setAnswer(item.id, value)}
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                ))}
                {showValidation && !allAnswered && (
                    <p className="mrsValidationMessage">Please answer every item before submitting.</p>
                )}
                <button type="button" className="mrsSubmitButton" onClick={handleSubmit}>Submit</button>
            </div>
            <p className="mrsCitation">
                Taylor MK, Venkatesh A, Campbell JS, et al. Development and Validation of the Military Readiness Scale in U.S. Naval Aircrew. <em>Mil Med</em>. Nov 1 2025;190(11-12):e2420-e2426. doi:10.1093/milmed/usaf193
            </p>
        </>
    );
}
